import { FC, useContext, useMemo, useCallback } from "react";
import { hasPermission } from "../../../helpers/hasPermisson";
import { IFunctionPermissions } from "../../../common/api_interfaces/role/IFunctionPermissions";
import { equalDMY } from "../../../helpers/compareDates";
import { AppContext } from "../../../App";
import { Context } from "../../..";
import { isAxiosError } from "axios";
import MessageItem from "./MessageLib/MessageItemProps";

import "./InputChat.css";
import { IMessage } from "../../../common/api_interfaces/chat/IMessage";
import { IMessageGroup } from "../../../common/api_interfaces/chat/IMessageGroup";
interface ChatMessagesProps {
    userAccess: IFunctionPermissions[];
    messageUrlQueryParams?: string;
    messageGroups: IMessageGroup[];
    idEditMessage: number;
    modeTextEditor: "edit" | "create";
    onDeleteMessage: () => void;
    onEditMessage: (message: IMessage) => void;
}

const ChatMessages: FC<ChatMessagesProps> = ({
    userAccess,
    messageUrlQueryParams,
    messageGroups,
    idEditMessage,
    modeTextEditor,
    onDeleteMessage,
    onEditMessage,
}) => {
    const { store } = useContext(Context);
    const { showToast } = useContext(AppContext);

    const canViewTaskChatMessage = useMemo(() => hasPermission(userAccess, "TaskChatAction", "view"), [userAccess]);
    const canDeleteTaskChatMessage = useMemo(() => hasPermission(userAccess, "TaskChatAction", "delete"), [userAccess]);

    const getCurrentDate = useCallback((messageDateCur: string): string => {
        const messageDate = new Date(messageDateCur);
        const today = new Date();
        const yesterday = new Date(today);
        yesterday.setDate(today.getDate() - 1);
        const dayBeforeYesterday = new Date(today);
        dayBeforeYesterday.setDate(today.getDate() - 2);

        if (equalDMY(messageDate, today)) {
            return "Сегодня";
        } else if (equalDMY(messageDate, yesterday)) {
            return "Вчера";
        } else if (equalDMY(messageDate, dayBeforeYesterday)) {
            return "Позавчера";
        } else {
            return `${messageDate.getDate().toString().padStart(2, "0")}.${(messageDate.getMonth() + 1)
                .toString()
                .padStart(2, "0")}.${messageDate.getFullYear()}`;
        }
    }, []);

    const handleCopyMessageUrl = useCallback(async (messageId: number) => {
        const url = `${window.location.origin}${window.location.pathname}${
            messageUrlQueryParams ? `?${messageUrlQueryParams}` : ""
        }#message-${messageId}`;
        try {
            await navigator.clipboard.writeText(url);
            showToast("Ссылка на сообщение скопирована");
        } catch (err) {
            showToast("Не удалось скопировать ссылку!");
        }
    }, [messageUrlQueryParams, showToast]);

    const deleteMessage = useCallback(async (idMessage: number) => {
        if (!canDeleteTaskChatMessage) {
            showToast("У вас нет прав на удаление комментариев");
            return;
        }

        try {
            await store.chatService.deleteMessage(idMessage);
            onDeleteMessage();
        } catch (err: any) {
            if (isAxiosError(err)) {
                showToast(err.response?.data?.message);
            }
        }
    }, [canDeleteTaskChatMessage, store.chatService, onDeleteMessage, showToast]);

    const getMessageStyle = useCallback((message: IMessage) => {
        const isCurrentMessage = window.location.hash === `#message-${message.id}`;
        const isEditing = modeTextEditor && message.id === idEditMessage;
        
        if (isEditing) {
            return isCurrentMessage 
                ? { backgroundColor: "rgba(188,217,239,0.57)" }
                : { backgroundColor: "lightblue" };
        }
        
        return isCurrentMessage 
            ? { backgroundColor: "rgba(188,217,239,0.57)" }
            : {};
    }, [modeTextEditor, idEditMessage]);

    if (!canViewTaskChatMessage) {
        return null;
    }

    return (
        <div className="input--chat__block--messages">
            {messageGroups.map((messageGroup, groupIndex) => (
                <div key={`group-${groupIndex}`} className="input--chat__block--group">
                    <div className="input--chat__block--messages__date">
                        <div className="input--chat__block--messages__date--line"></div>
                        <div className="input--chat__block--messages__date--num">
                            {getCurrentDate(messageGroup.date)}
                        </div>
                        <div className="input--chat__block--messages__date--line"></div>
                    </div>
                    
                    {messageGroup.messages.map((message) => (
                        <MessageItem
                            key={message.id}
                            message={message}
                            isOwnMessage={store.user.id === message.createdBy.id}
                            style={getMessageStyle(message)}
                            onCopyUrl={handleCopyMessageUrl}
                            onEdit={onEditMessage}
                            onDelete={deleteMessage}
                            canDelete={canDeleteTaskChatMessage}
                            showToast={showToast}
                        />
                    ))}
                </div>
            ))}
        </div>
    );
};

export default ChatMessages;